import type { Lead, LeadTag } from "@/types/lead";
import { normalizeSearch } from "@/lib/normalize-search";

/** Preserva channel/tags (e outros campos enriquecidos) ao substituir lista do Kanban. */
export function mergeLeadSnapshots<T extends Lead>(prev: T[], incoming: T[]): T[] {
  const prevById = new Map(prev.map((l) => [l.id, l]));
  return incoming.map((lead) => {
    const old = prevById.get(lead.id);
    if (!old) return lead;
    return {
      ...lead,
      channel: lead.channel ?? old.channel,
      tags: (lead.tags?.length ? lead.tags : old.tags) as LeadTag[] | undefined,
    };
  });
}

export function dedupeLeadsById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  return out;
}

export function normalizePhoneKey(phone: string | null | undefined): string {
  if (!phone) return "";
  return phone.replace(/\D/g, "");
}

function normalizeSearchToken(s: string): string {
  return normalizeSearch(s);
}

/** Termo para RPC `search_leads_by_term` (unaccent + telefone só com dígitos quando aplicável). */
export function prepareLeadSearchTerm(raw: string): string {
  const trimmed = raw.trim().split(",").join(" ");
  if (!trimmed) return "";
  const digits = trimmed.replace(/\D/g, "");
  const compact = trimmed.replace(/\s/g, "");
  if (digits.length >= 4 && digits.length / Math.max(compact.length, 1) >= 0.6) {
    return digits;
  }
  return trimmed;
}

/** Busca na tela de disparos: apenas nome e telefone (não email/metadata do RPC). */
export function leadMatchesDisparoSearch(
  lead: Pick<Lead, "name" | "phone">,
  rawQuery: string,
): boolean {
  const q = rawQuery.trim();
  if (!q) return true;
  const tokens = q.split(/\s+/).map(normalizeSearchToken).filter(Boolean);
  if (tokens.length === 0) return true;

  const name = normalizeSearchToken(lead.name ?? "");
  const phone = normalizeSearchToken(lead.phone ?? "");

  return tokens.every((token) => name.includes(token) || phone.includes(token));
}

/** Busca global do CRM (nome, email, telefone, tags). */
export function leadMatchesGlobalSearch(lead: Lead, rawQuery: string): boolean {
  const q = rawQuery.trim();
  if (!q) return true;
  const tokens = q.split(/\s+/).map(normalizeSearchToken).filter(Boolean);
  if (tokens.length === 0) return true;

  const name = normalizeSearchToken(lead.name ?? "");
  const email = normalizeSearchToken(lead.email ?? "");
  const phone = normalizeSearchToken(lead.phone ?? "");

  return tokens.every(
    (token) =>
      name.includes(token) ||
      email.includes(token) ||
      phone.includes(token) ||
      (lead.tags ?? []).some((t) => normalizeSearchToken(t.name).includes(token)),
  );
}

/**
 * Relevância do nome na busca: 0 = nome começa com termo, 1 = palavra começa,
 * 2 = substring, 3 = sem match no nome (ex.: match só por telefone).
 */
export function leadSearchRelevanceRank(
  name: string | null | undefined,
  rawTerm: string,
): number {
  const tokens = rawTerm
    .trim()
    .split(/\s+/)
    .map(normalizeSearchToken)
    .filter(Boolean);
  if (tokens.length === 0) return 3;

  const stripped = normalizeSearchToken((name ?? "").replace(/\s*\([^)]*\).*$/, ""));
  const full = normalizeSearchToken(name ?? "");
  const norms = [...new Set([stripped, full].filter(Boolean))];

  let worst = 0;
  for (const token of tokens) {
    let score = 3;
    if (stripped.startsWith(token)) {
      score = 0;
    } else if (norms.some((norm) => norm.split(/\s+/).some((word) => word.startsWith(token)))) {
      score = 1;
    } else if (norms.some((norm) => norm.includes(token))) {
      score = 2;
    }
    worst = Math.max(worst, score);
  }
  return worst;
}

/** Espelha `lead_first_word_prefix_length` no SQL. */
export function leadFirstWordPrefixLength(
  name: string | null | undefined,
  rawTerm: string,
): number {
  const token = rawTerm
    .trim()
    .split(/\s+/)
    .map(normalizeSearchToken)
    .find(Boolean);
  if (!token) return 0;

  const stripped = normalizeSearchToken((name ?? "").replace(/\s*\([^)]*\).*$/, ""));
  const firstWord = stripped.split(/\s+/)[0] ?? "";
  return firstWord.startsWith(token) ? token.length : 0;
}

/** Espelha `lead_name_earliest_token_position` no SQL (1-based, pior token define). */
export function leadNameEarliestTokenPosition(
  name: string | null | undefined,
  rawTerm: string,
): number {
  const tokens = rawTerm
    .trim()
    .split(/\s+/)
    .map(normalizeSearchToken)
    .filter(Boolean);
  if (tokens.length === 0) return Number.MAX_SAFE_INTEGER;

  const stripped = normalizeSearchToken((name ?? "").replace(/\s*\([^)]*\).*$/, ""));
  const full = normalizeSearchToken(name ?? "");
  const norms = [...new Set([stripped, full].filter(Boolean))];

  let worst = 0;
  for (const token of tokens) {
    let best = Number.MAX_SAFE_INTEGER;
    for (const norm of norms) {
      const idx = norm.indexOf(token);
      if (idx >= 0) best = Math.min(best, idx + 1);
    }
    worst = Math.max(worst, best);
  }
  return worst;
}

export function sortLeadsBySearchRelevance<T extends { name: string | null; created_at?: string }>(
  rows: T[],
  rawTerm: string,
): T[] {
  const term = rawTerm.trim();
  if (!term) return rows;
  return [...rows].sort((a, b) => {
    const rankDiff = leadSearchRelevanceRank(a.name, term) - leadSearchRelevanceRank(b.name, term);
    if (rankDiff !== 0) return rankDiff;

    const prefixDiff =
      leadFirstWordPrefixLength(b.name, term) - leadFirstWordPrefixLength(a.name, term);
    if (prefixDiff !== 0) return prefixDiff;

    const posDiff =
      leadNameEarliestTokenPosition(a.name, term) - leadNameEarliestTokenPosition(b.name, term);
    if (posDiff !== 0) return posDiff;

    if (a.created_at && b.created_at) {
      const createdDiff = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      if (createdDiff !== 0) return createdDiff;
    }

    return (a.name ?? "").localeCompare(b.name ?? "", "pt-BR");
  });
}

/** Autocomplete: no máximo um lead por telefone (mais recente). */
export function dedupeSearchPreviewByPhone<T extends Lead>(rows: T[]): T[] {
  const byPhone = new Map<string, T>();
  const noPhone: T[] = [];

  for (const lead of rows) {
    const key = normalizePhoneKey(lead.phone);
    if (!key) {
      noPhone.push(lead);
      continue;
    }
    const existing = byPhone.get(key);
    if (!existing) {
      byPhone.set(key, lead);
      continue;
    }
    const a = new Date(lead.created_at).getTime();
    const b = new Date(existing.created_at).getTime();
    if (a >= b) byPhone.set(key, lead);
  }

  return dedupeLeadsById([...noPhone, ...byPhone.values()]);
}
